import React, { useCallback, useEffect, useState } from 'react';
import CupMark from './CupMark.jsx';

const STORAGE_KEY = 'sidebar_collapsed';

function getInitialCollapsedState() {
  if (typeof window === 'undefined') { return false; }
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'true';
  } catch (err) {
    return false;
  }
}

/** Shared desktop behavior for the portal and console rails. */
export function useCollapsibleRail() {
  const [collapsed, setCollapsed] = useState(getInitialCollapsedState);

  const toggle = useCallback(() => {
    setCollapsed((current) => {
      const next = !current;
      try {
        window.localStorage.setItem(STORAGE_KEY, String(next));
      } catch (err) {
        // Storage is a convenience; the control still works without it.
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === '.') {
        event.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => { window.removeEventListener('keydown', handleKeyDown); };
  }, [toggle]);

  return { collapsed, toggle };
}

export function SidebarPanelIcon({ size = 20, color = 'currentColor' }) {
  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="4" width="18" height="16" rx="2.5" />
      <path d="M9 4v16" />
    </svg>
  );
}

/**
 * Logo row with the same fixed geometry as the main client: the 32px cup stays
 * anchored 16px from the rail edge in both states. Collapsing only removes the
 * wordmark and close control; it never recenters the cup.
 */
export function CollapsibleRailHeader({ collapsed, onToggle, t }) {
  return (
    <div
      className="px-3 pb-6 border-b flex items-center gap-2 min-h-[53px]"
      style={{ borderColor: t.hairline }}
    >
      {collapsed ? (
        <button
          type="button"
          onClick={onToggle}
          aria-label="Open sidebar"
          title="Open sidebar"
          className={`group relative h-8 w-8 shrink-0 rounded-md flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 ${t.focusRing}`}
        >
          <span className="absolute inset-0 flex items-center justify-center opacity-100 group-hover:opacity-0 transition-opacity">
            <CupMark size={32} />
          </span>
          <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: t.muted }}>
            <SidebarPanelIcon />
          </span>
        </button>
      ) : (
        <>
          {/* flex-1 + min-w-0 lets the mark use leftover width; the toggle stays
              shrink-0 so "Coffee Chats." never paints under the panel icon
              (w-56 was ~16px short at wordmarkSize 18). */}
          <div className="min-w-0 flex-1 overflow-hidden">
            <CupMark size={32} wordmarkSize={16} withWordmark />
          </div>
          <button
            type="button"
            onClick={onToggle}
            aria-label="Close sidebar"
            title="Close sidebar"
            className={`h-8 w-8 shrink-0 rounded-md flex items-center justify-center transition-opacity hover:opacity-70 focus:outline-none focus-visible:ring-2 ${t.focusRing}`}
            style={{ color: t.muted }}
          >
            <SidebarPanelIcon />
          </button>
        </>
      )}
    </div>
  );
}

const ICONS = {
  overview: (
    <>
      <rect x="4" y="4" width="6" height="6" rx="1" />
      <rect x="14" y="4" width="6" height="6" rx="1" />
      <rect x="4" y="14" width="6" height="6" rx="1" />
      <rect x="14" y="14" width="6" height="6" rx="1" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3" />
      <path d="M3.5 19c.5-3.5 2.3-5 5.5-5s5 1.5 5.5 5" />
      <path d="M15 5.5a3 3 0 0 1 0 5.8M16 14c2.6.3 4 1.8 4.5 4.5" />
    </>
  ),
  support: <path d="M5 18.5 3.8 21l3.2-.8A9 9 0 1 0 5 18.5Z" />,
  institution: (
    <>
      <path d="m3 9 9-5 9 5" />
      <path d="M5 10v8M9.5 10v8M14.5 10v8M19 10v8M3 20h18" />
    </>
  ),
  audit: (
    <>
      <path d="M7 3h7l4 4v14H7z" />
      <path d="M14 3v5h5M10 13h5M10 17h5" />
    </>
  ),
  financials: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M15.5 8.5c-.8-.7-1.8-1-3-1-1.7 0-3 .9-3 2.2 0 3.5 6 1.4 6 4.8 0 1.3-1.3 2.3-3.2 2.3-1.3 0-2.5-.4-3.3-1.2M12 5.5v13" />
    </>
  ),
  analytics: (
    <>
      <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
    </>
  ),
  network: (
    <>
      <circle cx="5" cy="6" r="2.5" />
      <circle cx="19" cy="6" r="2.5" />
      <circle cx="12" cy="18" r="2.5" />
      <path d="M7.3 7.4 10.6 16M16.7 7.4 13.4 16M7.5 6h9" />
    </>
  ),
  seats: (
    <>
      <circle cx="8" cy="8" r="3" />
      <path d="M2.5 19c.5-3.5 2.3-5 5.5-5s5 1.5 5.5 5M17 8v6M14 11h6" />
    </>
  ),
  branding: (
    <>
      <path d="M12 3 14.7 8.5 21 9.4l-4.5 4.4 1.1 6.2-5.6-3-5.6 3 1.1-6.2L3 9.4l6.3-.9z" />
    </>
  ),
  ai: (
    <>
      <path d="m12 3 1.2 3.8L17 8l-3.8 1.2L12 13l-1.2-3.8L7 8l3.8-1.2zM18 14l.8 2.2L21 17l-2.2.8L18 20l-.8-2.2L15 17l2.2-.8z" />
      <path d="M4 13h5v5H6l-2 2z" />
    </>
  ),
  sparkles: (
    <>
      <path d="m12 3 1.2 3.8L17 8l-3.8 1.2L12 13l-1.2-3.8L7 8l3.8-1.2Z" />
      <path d="m18 14 .8 2.2L21 17l-2.2.8L18 20l-.8-2.2L15 17l2.2-.8Z" />
    </>
  ),
  avatar: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4.5 21c.6-4.7 3-7 7.5-7s6.9 2.3 7.5 7" />
      <path d="M18.5 4.5v4M16.5 6.5h4" />
    </>
  ),
  cycle: (
    <>
      <path d="M20 12a8 8 0 0 1-13.66 5.66M4 12a8 8 0 0 1 13.66-5.66" />
      <path d="M18 3v4h-4M6 21v-4h4" />
    </>
  ),
  alert: (
    <>
      <path d="M12 4 2.8 19.5a1 1 0 0 0 .9 1.5h16.6a1 1 0 0 0 .9-1.5Z" />
      <path d="M12 10v4.5M12 17.5v.01" />
    </>
  ),
  badge: (
    <>
      <rect x="5" y="3" width="14" height="18" rx="2" />
      <path d="M10 3v2.5h4V3" />
      <circle cx="12" cy="11" r="2.5" />
      <path d="M8.5 17.5c.6-1.8 1.8-2.7 3.5-2.7s2.9.9 3.5 2.7" />
    </>
  ),
  lock: (
    <>
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7.5a4 4 0 0 1 8 0V11" />
    </>
  ),
};

export function RailNavIcon({ name, size = 20 }) {
  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0"
    >
      {ICONS[name] || ICONS.overview}
    </svg>
  );
}
